/** 书库：接着读、全部资料（读到哪里）、导入、收件箱（导师代存的资料要你一键收下）。 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, FileText, Globe, Inbox } from "lucide-react";
import { useState } from "react";
import { AddSource } from "@/features/library/AddSource";
import { errorMessage } from "@/shared/api/client";
import type { SourceOut } from "@/shared/api/gen/types.gen";
import { useAction, useJobs, useSources } from "@/shared/api/hooks";
import { useScene } from "@/shared/scene";
import { Button, Card, Empty, ErrorNote, PageHeader, Pill, Spinner } from "@/shared/ui";

export const Route = createFileRoute("/")({ component: Library });

const FORMAT: Record<string, string> = { epub: "EPUB", pdf: "PDF", md: "Markdown", text: "网页 / 文字" };

function Library() {
  useScene({ page: "书库" });
  const active = useSources("active");
  const inbox = useSources("inbox");
  const [adding, setAdding] = useState(false);
  const books = active.data ?? [];
  const last = [...books]
    .filter((b) => b.last_read_at)
    .sort((a, b) => (b.last_read_at ?? "").localeCompare(a.last_read_at ?? ""))[0];

  return (
    <div>
      <PageHeader title="书库">
        <Button variant="primary" onClick={() => setAdding(!adding)} aria-expanded={adding}>
          导入
        </Button>
      </PageHeader>
      <div className="flex flex-col gap-4 px-4 md:px-7">
        {adding ? (
          <Card className="p-4">
            <AddSource />
          </Card>
        ) : null}
        {last ? <ContinueCard book={last} /> : null}
        {(inbox.data ?? []).length ? <InboxCard items={inbox.data ?? []} /> : null}
        {active.isPending ? (
          <div className="grid place-items-center py-10 text-text-3">
            <Spinner />
          </div>
        ) : books.length ? (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {books.map((b) => (
              <li key={b.id}>
                <BookCard book={b} />
              </li>
            ))}
          </ul>
        ) : (
          <Empty title="书库还是空的">
            点右上角“导入”，放进一本 EPUB、PDF 或 Markdown；也可以收录网页或一段文字。
          </Empty>
        )}
      </div>
    </div>
  );
}

function Progress({ value }: { value: number | null }) {
  const pct = Math.round((value ?? 0) * 100);
  return (
    <div className="flex items-center gap-2 text-[12px] text-text-3">
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2"
        role="progressbar"
        aria-label="读到"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-full bg-signal" style={{ width: `${pct}%` }} />
      </div>
      {value === null ? "未读" : `${pct}%`}
    </div>
  );
}

function ContinueCard({ book }: { book: SourceOut }) {
  return (
    <Card className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="text-[12px] text-text-3">接着读</div>
        <div className="truncate text-[16px] font-semibold">《{book.title}》</div>
        <Progress value={book.progress} />
      </div>
      <Link
        to="/read/$sourceId"
        params={{ sourceId: book.id }}
        className="inline-flex h-9 items-center justify-center gap-1.5 rounded-s bg-action px-4 text-[13.5px] text-action-text hover:opacity-90"
      >
        <BookOpen className="size-4" />
        接着读
      </Link>
    </Card>
  );
}

function BookCard({ book }: { book: SourceOut }) {
  const jobs = useJobs();
  const busy = (jobs.data ?? []).find(
    (j) => j.subject === book.id && (j.status === "queued" || j.status === "running"),
  );
  const failed = book.parse?.status === "failed" || book.parse?.status === "empty";
  const Icon = book.origin === "url" ? Globe : FileText;
  const body = (
    <Card className="flex h-full flex-col gap-2.5 p-4 hover:border-border-strong">
      <div className="flex items-start gap-2.5">
        <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-text-3" />
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 font-medium">{book.title}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {book.view ? <Pill>{FORMAT[book.view]}</Pill> : null}
            {book.parse?.pages ? <Pill>{book.parse.pages} 页</Pill> : null}
          </div>
        </div>
      </div>
      {busy ? (
        <div className="flex items-center gap-2 text-[12.5px] text-text-2">
          <Spinner /> {busy.stage || "排队中"}
        </div>
      ) : failed ? (
        <div className="text-[12.5px] text-warn">{book.parse?.warnings[0] ?? "没有读到正文"}</div>
      ) : (
        <Progress value={book.progress} />
      )}
    </Card>
  );
  if (!book.view || busy) return body;
  return (
    <Link to="/read/$sourceId" params={{ sourceId: book.id }} aria-label={`读《${book.title}》`}>
      {body}
    </Link>
  );
}

function InboxCard({ items }: { items: SourceOut[] }) {
  const accept = useAction<{ source_id: string }>("library.accept_inbox");
  const reject = useAction<{ source_id: string }>("library.reject_inbox");
  return (
    <Card className="flex flex-col gap-2.5 p-4">
      <div className="flex items-center gap-2 font-semibold">
        <Inbox className="size-4" /> 收件箱
        <span className="text-[12.5px] font-normal text-text-3">
          导师代你收录的资料，收下后才能阅读和检索
        </span>
      </div>
      {accept.error || reject.error ? (
        <ErrorNote>{errorMessage(accept.error ?? reject.error)}</ErrorNote>
      ) : null}
      <ul className="flex flex-col gap-1.5">
        {items.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-2 text-[13.5px]">
            <span className="min-w-0 flex-1 truncate">{s.title}</span>
            <Button size="sm" variant="primary" onClick={() => accept.mutate({ source_id: s.id })}>
              收下
            </Button>
            <Button size="sm" variant="ghost" onClick={() => reject.mutate({ source_id: s.id })}>
              不要
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
